/* eslint-disable react-refresh/only-export-components */
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '@/hooks/useAuth';
import { fetchWithRetry } from '@/lib/apiRequest';

import type {
  Client,
  CreateClientInput,
} from '@/types/client';

type ContextUser = {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'lawyer' | 'secretary';
  status: string;
};

interface ApiClient {
  id: string;
  created_at?: string | null;
  updated_at?: string | null;
  person_type?: string | null;
  name?: string | null;
  cpf_cnpj?: string | null;
  rg?: string | null;
  issuing_authority?: string | null;
  nationality?: string | null;
  civil_status?: string | null;
  profession?: string | null;
  birth_date?: string | null;
  phone?: string | null;
  email?: string | null;
  zip_code?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  mother_name?: string | null;
  father_name?: string | null;
  legal_representative_name?: string | null;
  legal_representative_cpf?: string | null;
  legal_representative_role?: string | null;
  responsible_user?: string | null;
  origin?: string | null;
  status?: string | null;
  drive_folder_url?: string | null;
  notes?: string | null;
}

interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'lawyer' | 'secretary';
  status: string;
}

interface AppContextType {
  clients: Client[];
  clientsLoading: boolean;
  clientsError: string | null;

  users: ContextUser[];

  createClient: (
    input: CreateClientInput
  ) => Promise<Client>;

  updateClient: (
    id: string,
    input: CreateClientInput
  ) => Promise<Client>;

  deleteClient: (
    id: string
  ) => Promise<void>;
}

const mapApiStatusToClientStatus = (
  status?: string | null
) => {
  if (
    !status ||
    status === 'active'
  ) {
    return 'Ativo';
  }

  if (
    status === 'inactive'
  ) {
    return 'Inativo';
  }

  // Legacy operational labels are derived indicators, not client lifecycle values.
  return 'Ativo';
};

const mapApiClientToClient = (
  api: ApiClient
): Client => {
  const name =
    api.name?.trim() ||
    'Sem nome';

  const city = [
    api.city,
    api.state,
  ]
    .filter(Boolean)
    .join(' - ');

  return {
    id: api.id,
    createdAt: api.created_at ?? undefined,
    updatedAt: api.updated_at ?? undefined,

    personType:
      api.person_type ||
      'PF',

    name,

    cpfCnpj:
      api.cpf_cnpj ||
      '',

    rg:
      api.rg ||
      '',

    issuingAuthority:
      api.issuing_authority ||
      '',

    nationality:
      api.nationality ||
      '',

    civilStatus:
      api.civil_status ||
      '',

    profession:
      api.profession ||
      '',

    birthDate:
      api.birth_date ||
      '',

    phone:
      api.phone ||
      '',

    email:
      api.email ||
      '',

    zipCode:
      api.zip_code ||
      '',

    street:
      api.street ||
      '',

    number:
      api.number ||
      '',

    complement:
      api.complement ||
      '',

    neighborhood:
      api.neighborhood ||
      '',

    cityName:
      api.city ||
      '',

    city,

    state:
      api.state ||
      '',

    motherName:
      api.mother_name ||
      '',

    fatherName:
      api.father_name ||
      '',

    legalRepresentativeName:
      api.legal_representative_name ||
      '',

    legalRepresentativeCpf:
      api.legal_representative_cpf ||
      '',

    legalRepresentativeRole:
      api.legal_representative_role ||
      '',

    origin:
      api.origin ||
      '',

    notes:
      api.notes ||
      '',

    casesCount: 0,
    pendingPayments: false,
    pendingDocs: false,

    driveFolder:
      api.drive_folder_url ||
      '',

    responsible:
      api.responsible_user ||
      '',

    status:
      mapApiStatusToClientStatus(
        api.status
      ),

    documents: [],
    cases: [],

    hasContract: false,
    hasPowerAttorney: false,
  };
};

const AppContext =
  createContext<AppContextType | null>(
    null
  );

export const useAppContext = () => {
  const context =
    useContext(
      AppContext
    );

  if (!context) {
    throw new Error(
      'useAppContext must be within AppProvider'
    );
  }

  return context;
};

export const AppProvider: FC<{
  children: ReactNode;
}> = ({
  children,
}) => {
  const {
    user: authUser,
    loading: authLoading,
  } = useAuth();

  const [
    clients,
    setClients,
  ] = useState<Client[]>([]);

  const [
    clientsLoading,
    setClientsLoading,
  ] = useState(true);

  const [
    clientsError,
    setClientsError,
  ] = useState<string | null>(
    null
  );

  const [
    users,
    setUsers,
  ] = useState<ContextUser[]>(
    []
  );

  const loadClients =
    useCallback(
      async () => {
        if (!authUser) {
          setClients([]);
          setClientsError(null);
          setClientsLoading(false);
          return;
        }

        setClientsLoading(true);
        setClientsError(null);

        try {
          const response =
            await fetchWithRetry(
              '/api/clients',
              {
                credentials:
                  'same-origin',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.clients
            )
          ) {
            throw new Error(
              data.error ||
                `GET /api/clients falhou com status ${response.status}`
            );
          }

          setClients(
            (
              data.clients as
                ApiClient[]
            ).map(
              mapApiClientToClient
            )
          );
        } catch (error) {
          console.error(
            'Erro ao carregar clientes reais.',
            error
          );

          setClients([]);

          setClientsError(
            'Não foi possível carregar os clientes. Tente novamente.'
          );
        } finally {
          setClientsLoading(
            false
          );
        }
      },
      [authUser]
    );

  const loadUsers =
    useCallback(
      async () => {
        if (!authUser) {
          setUsers([]);
          return;
        }

        const authenticatedUser: ContextUser = {
          id:
            authUser.id,

          email:
            authUser.email,

          name:
            authUser.name,

          role:
            authUser.role,

          status:
            'active',
        };

        if (
          authUser.role !==
          'admin'
        ) {
          setUsers([
            authenticatedUser,
          ]);

          return;
        }

        try {
          const response =
            await fetchWithRetry(
              '/api/users',
              {
                credentials:
                  'same-origin',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.users
            )
          ) {
            throw new Error(
              data.error ||
                `GET /api/users falhou com status ${response.status}`
            );
          }

          const realUsers =
            (
              data.users as
                ApiUser[]
            )
              .filter(
                (user) =>
                  user.status ===
                  'active'
              )
              .map(
                (user) => ({
                  id:
                    user.id,

                  email:
                    user.email,

                  name:
                    user.name,

                  role:
                    user.role,

                  status:
                    user.status,
                })
              );

          setUsers(
            realUsers.length >
              0
              ? realUsers
              : [
                  authenticatedUser,
                ]
          );
        } catch (error) {
          console.error(
            'Erro ao carregar usuarios reais.',
            error
          );

          setUsers([
            authenticatedUser,
          ]);
        }
      },
      [authUser]
    );

  useEffect(
    () => {
      if (authLoading) {
        return;
      }

      void loadClients();
      void loadUsers();
    },
    [
      authLoading,
      loadClients,
      loadUsers,
    ]
  );

  const createClient =
    useCallback(
      async (
        input:
          CreateClientInput
      ) => {
        const response =
          await fetchWithRetry(
            '/api/clients',
            {
              method:
                'POST',

              credentials:
                'same-origin',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.client
        ) {
          throw new Error(
            data.error ||
              `POST /api/clients falhou com status ${response.status}`
          );
        }

        const created =
          mapApiClientToClient(
            data.client
          );

        setClients(
          (previous) => [
            created,

            ...previous.filter(
              (client) =>
                client.id !==
                created.id
            ),
          ]
        );

        return created;
      },
      []
    );

  const updateClient =
    useCallback(
      async (
        id: string,
        input:
          CreateClientInput
      ) => {
        const response =
          await fetchWithRetry(
            `/api/clients/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'PUT',

              credentials:
                'same-origin',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.client
        ) {
          throw new Error(
            data.error ||
              `PUT /api/clients/${id} falhou com status ${response.status}`
          );
        }

        const updated =
          mapApiClientToClient(
            data.client
          );

        setClients(
          (previous) =>
            previous.map(
              (client) =>
                client.id ===
                id
                  ? updated
                  : client
            )
        );

        return updated;
      },
      []
    );

  const deleteClient =
    useCallback(
      async (
        id: string
      ) => {
        const response =
          await fetchWithRetry(
            `/api/clients/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'DELETE',

              credentials:
                'same-origin',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              `DELETE /api/clients/${id} falhou com status ${response.status}`
          );
        }

        setClients(
          (previous) =>
            previous.filter(
              (client) =>
                client.id !== id
            )
        );
      },
      []
    );

  const value =
    useMemo<AppContextType>(
      () => ({
        clients,
        clientsLoading,
        clientsError,
        users,
        createClient,
        updateClient,
        deleteClient,
      }),
      [
        clients,
        clientsLoading,
        clientsError,
        users,
        createClient,
        updateClient,
        deleteClient,
      ]
    );

  return (
    <AppContext.Provider
      value={value}
    >
      {children}
    </AppContext.Provider>
  );
};
